import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { OrderInBatch, PrintBatchData } from '@app/services/batches/print-batches';
import { DeliveryPromise, DeliveryPromiseLabel, Order } from '@app/services/order/order.model';
import { PrintService } from '../../services/print.service';
import { ScanService } from '../../services/scan.service';
import { ViewprintingjobsComponent } from '../viewprintingjobs/viewprintingjobs.component';
import { OrderBatchService } from '../../../../services/order-batch/order-batch.service';
import { FreightServiceType, ShippingStatus, StatusFilters } from '@pdqdev/pdq-shared/dist/CarrierData';
import moment from 'moment';

import { TranslatedFulfillmentStatus } from '@pdqdev/pdq-shared/dist/OrderData';
import { ShopService } from '@app/services/shops/shop.service';
import { getOrderSplitIndicesString } from '@utils/order-split-utils';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-batch-orders-modal',
  templateUrl: './batch-orders-modal.component.html',
  styleUrls: ['./batch-orders-modal.component.scss'],
  standalone: false,
})
export class BatchOrdersModalComponent implements OnInit {
  @Input() batchOrders: PrintBatchData;
  readonly displayedColumns: string[] = [
    'batchId',
    'orderNumber',
    'itemCount',
    'shippingId',
    // 'lastPrint',
    'expectedDate',
    'fulfillmentStatus',
  ];
  readonly TranslatedFulfillmentStatus = TranslatedFulfillmentStatus;
  numberOfPackages: number;
  loadingOrder: string;
  scannedOrders: string[] = [];
  // TODO: refactor to pass batchStatus as Input()
  batchStatuses = ['running', 'printing', 'recentlyDone'];
  orderIdsForCountPackages: any = [];
  shouldDisplayPreviewButton: boolean;
  isTplCarrier: boolean = false;
  isBatchOnFulfillment: boolean = false;
  ordersSplitIndices = {};

  constructor(
    private printService: PrintService,
    private scanService: ScanService,
    private dialog: MatDialog,
    private orderBatchService: OrderBatchService,
    private _shopService: ShopService,
  ) {}

  ngOnInit(): void {
    this.isTplCarrier = this.batchOrders.selectedCarrier?.freightServiceType?.includes(
      FreightServiceType.FULFILLMENT_SERVICE_3PL,
    );

    this.getNumberOfPackages();
    this.batchOrders.orders.forEach((order) => {
      this.calculateDeliveryPromiseLabel(order);
      this.ordersSplitIndices[order.id] = getOrderSplitIndicesString(order as any, 'view');
    });
    this.scanService.scanOrderSuccessDialogOpened$.pipe(untilDestroyed(this)).subscribe((order) => {
      if (order) {
        const updatedOrders = this.printService.getCurrenBatchOrders();
        // this.batchOrders.orders = [...updatedOrders.orders];
        this.scannedOrders = [...this.scannedOrders, order.orderNumber];
      } else {
        if (this.scannedOrders.length === this.batchOrders.orders.length) {
          this.printService.resetCurrenttBatchOrders();
          this.printService.scannedBatch = this.batchOrders;
          this.scanService.toggleScanBatchSuccessDialog(this.batchOrders);
        }
      }
    });
    this.batchOrders.total;

    this.shouldDisplayPreviewButton = !!this.batchOrders?.selectedCarrier?.isMultiCarrier;
  }

  /**
   * TODO: refactor to open this dialog from a separate component/service.
   * This is just a copied method openViewPrintingJob from prinbatch.component
   */
  previewOrder(order: OrderInBatch): void {
    if (!this.isTplCarrier) {
      this.dialog.open(ViewprintingjobsComponent, {
        maxWidth: '100vw',
        width: '45vw',
        maxHeight: '100vh',
        height: '65vh',
        backdropClass: 'global_modal_overlay',
        panelClass: 'viewprintjobslabels',
        data: order,
      });
    }
  }

  onFulfillBatch(batchId: number) {
    this.isBatchOnFulfillment = true;
    const orderIds: string[] = this.batchOrders.orders.map((order) => order.id);
    this.printService.fulfillOrders(orderIds).subscribe((result) => {
      this.isBatchOnFulfillment = false;
      result.forEach((res) => {
        const orderInsideBatch = this.batchOrders.orders.find((order) => order.id == res.id);
        orderInsideBatch.fulfillmentStatus = res.fulfillmentStatus;
      });
    });
  }

  checkIfToDisplayPreviewButton() {
    return !!this.batchOrders?.selectedCarrier?.isMultiCarrier;
  }

  checkAbilityToReturnOrder(row: Order, batch: PrintBatchData): boolean {
    return (
      !(
        StatusFilters.IN_TRANSIT.includes(row.deliveryTransaction?.translatedShippingStatus as ShippingStatus) ||
        StatusFilters.DELIVERED.includes(row.deliveryTransaction?.translatedShippingStatus as ShippingStatus)
      ) && moment(batch.createdDate) > moment().add(-3, 'day')
    );
  }

  getNumberOfPackages() {
    this.orderBatchService
      .getNumberOfPackages(this.batchOrders.id)
      .then((response) => {
        this.numberOfPackages = response;
      })
      .catch((err) => {
        return `cannot get number of packages for this orders ${this.batchOrders.id}`;
      });
  }

  scanOrder(orderId: string, shippingId: string): void {
    if (!this.loadingOrder && !this.isTplCarrier && !this.isBatchOnFulfillment) {
      this.loadingOrder = orderId;
      this.scanService
        .syncAndScanOrder(orderId, shippingId)
        .pipe(
          untilDestroyed(this),
          finalize(() => {
            this.loadingOrder = undefined;
          }),
        )
        .subscribe((order) => {
          this.scanService.toggleScanOrderDialogOpened(order);
        });
    }
  }

  onOrderMoved(movedOrder: OrderInBatch): void {
    this.batchOrders.orders = this.batchOrders.orders.filter((order) => order.id !== movedOrder.id);
  }

  calculateDeliveryPromiseLabel(order: OrderInBatch) {
    let countDownDate;
    let deliveryPromise: DeliveryPromise;
    let isPickUp;
    if (order.deliveryDate) {
      isPickUp = new Date(order.deliveryDate).getTime() == new Date(0).getTime();
      deliveryPromise = isPickUp
        ? { time: 0 }
        : {
            time: new Date(order.deliveryDate),
            default: order.isDefaultDeliveryDate,
          };
      countDownDate = isPickUp ? null : (deliveryPromise.time as Date).getTime();
    } else {
      deliveryPromise = { time: null };
      countDownDate = null;
    }
    const endDay = new Date();
    const startDay = new Date();
    endDay.setHours(23, 59, 59, 999);
    startDay.setHours(0, 0, 0, 0);
    const endDayTomorrow = new Date(endDay);
    endDayTomorrow.setDate(endDay.getDate() + 1);
    if (countDownDate == null && isPickUp) {
      deliveryPromise = {
        ...deliveryPromise,
        label: DeliveryPromiseLabel.PICKUP,
        print: DeliveryPromiseLabel.PICKUP,
      };
    } else if (countDownDate == null) {
      deliveryPromise = {
        ...deliveryPromise,
        label: DeliveryPromiseLabel.UNKNOWN,
        print: DeliveryPromiseLabel.UNKNOWN,
      };
    } else if (countDownDate < endDay.getTime() && countDownDate > startDay.getTime()) {
      deliveryPromise = {
        ...deliveryPromise,
        label: DeliveryPromiseLabel.TODAY,
        print: DeliveryPromiseLabel.TODAY,
      };
    } else if (countDownDate > endDay.getTime() && countDownDate < endDayTomorrow.getTime()) {
      deliveryPromise = {
        ...deliveryPromise,
        label: DeliveryPromiseLabel.TOMORROW,
        print: DeliveryPromiseLabel.TOMORROW,
      };
    } else if (countDownDate > endDayTomorrow.getTime()) {
      const numberOfDays = Math.floor((countDownDate - startDay.getTime()) / (36e5 * 24));
      deliveryPromise = {
        ...deliveryPromise,
        label: DeliveryPromiseLabel.IN_X_DAYS,
        print: `In ${numberOfDays} Days`,
      };
    } else if (countDownDate < startDay.getTime()) {
      const numberOfDays = Math.floor((countDownDate - startDay.getTime()) / (36e5 * 24));
      deliveryPromise = {
        ...deliveryPromise,
        label: DeliveryPromiseLabel.LATE_X_DAYS,
        print: `Late ${numberOfDays} Days`,
      };
    }
    order.deliveryPromise = deliveryPromise;
  }
}
